/**
 * Connections — Layout route
 *
 * Wraps all /connections/* child routes with ContentHeader + tab navigation.
 * Tabs: MCP Servers, API Connections, Functions, Marketplace, Health Monitor
 *
 * Child routes:
 *   /connections/              — MCP Servers (default)
 *   /connections/api           — API Connections
 *   /connections/functions      — Custom Functions (code editor)
 *   /connections/marketplace    — MCP Marketplace (browse + install)
 *   /connections/health        — Health Monitor
 *   /connections/$connectionId — Connection Detail
 *   /connections/new-mcp       — Register MCP Server
 *   /connections/new-api       — Add API Connection
 *   /connections/new-function  — Create Custom Function
 */

import { useState } from 'react';
import { createFileRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { ContentHeader } from '@/components/content-header';
import { Plug, Globe, Activity, Plus, Server, FileJson, Code, Store, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { TeamsMeetingBotWizard } from '@/components/connections/wizards/teams-meeting-bot-wizard';
import { useConnectionsCapabilities, useMeetingBotFeature } from '@/lib/use-connections-module';
import { ConnectionsRoleGate } from './connections/-connections-role-gate';

export const Route = createFileRoute('/connections')({
  component: ConnectionsLayout,
});

function ConnectionsLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [meetingBotWizardOpen, setMeetingBotWizardOpen] = useState(false);
  const meetingBotFeature = useMeetingBotFeature();
  const capabilities = useConnectionsCapabilities();
  const meetingBotEnabled = meetingBotFeature.data?.enabled === true;
  const apiConnectionsEnabled =
    capabilities.data?.adapters.restApi.enabled === true &&
    capabilities.data?.adapters.graphqlApi.enabled === true;
  const apiUnavailableReason = capabilities.isError
    ? 'Capability check failed'
    : capabilities.isLoading
      ? 'Checking access...'
      : !apiConnectionsEnabled
        ? capabilities.data?.adapters.restApi.reason ??
          'External API connections are unavailable in this deployment.'
        : null;
  const meetingBotUnavailableReason = meetingBotFeature.isError
    ? 'Feature check failed'
    : meetingBotFeature.isLoading
      ? 'Checking access...'
      : !meetingBotEnabled
        ? 'Disabled for this tenant'
        : null;

  // Show tabs only on main tab pages, hide on detail/editor sub-pages
  const TAB_PATHS = [
    '/connections', '/connections/',
    '/connections/api', '/connections/api/',
    '/connections/functions', '/connections/functions/',
    '/connections/marketplace', '/connections/marketplace/',
    '/connections/health', '/connections/health/',
  ];
  const isTabPage = TAB_PATHS.includes(location.pathname);

  return (
    <div className="flex flex-col h-full">
      {isTabPage && (
        <ContentHeader
          title="Connections"
          tabs={[
            { label: 'MCP Servers', path: '/connections', icon: Plug },
            { label: 'API Connections', path: '/connections/api', icon: Globe },
            { label: 'Functions', path: '/connections/functions', icon: Code },
            { label: 'Marketplace', path: '/connections/marketplace', icon: Store },
            { label: 'Health', path: '/connections/health', icon: Activity },
          ]}
          actions={
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="gap-1.5">
                  <Plus className="h-3.5 w-3.5" /> Add Connection
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                {/* bug #3 — UI-SPEC Add Connection menu. Grouped by flavor:
                    Flavor 1/2 (external MCP + marketplace + coming-soon custom
                    MCP type), a separator, then API + Function. */}
                <DropdownMenuLabel className="text-[10px] font-medium text-muted-foreground">
                  MCP servers
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => navigate({ to: '/connections/new-mcp' })}>
                  <Server className="h-4 w-4 mr-2" />
                  Connect external MCP
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate({ to: '/connections/marketplace' })}>
                  <Store className="h-4 w-4 mr-2" />
                  Browse Marketplace
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled
                  title="Authoring a custom MCP server type from a spec is coming soon."
                  onSelect={(event) => event.preventDefault()}
                  className="text-muted-foreground"
                >
                  <FileJson className="h-4 w-4 mr-2" />
                  <div className="flex min-w-0 flex-col">
                    <span>Author custom MCP type</span>
                    <span className="truncate text-[10px] leading-3 text-muted-foreground">
                      Coming soon
                    </span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] font-medium text-muted-foreground">
                  Direct connections
                </DropdownMenuLabel>
                <DropdownMenuItem
                  disabled={!apiConnectionsEnabled}
                  title={apiUnavailableReason ?? 'Add API Connection'}
                  onSelect={(event) => {
                    if (!apiConnectionsEnabled) {
                      event.preventDefault();
                      return;
                    }
                    navigate({ to: '/connections/new-api' });
                  }}
                >
                  <FileJson className="h-4 w-4 mr-2" />
                  <div className="flex min-w-0 flex-col">
                    <span>Add API Connection</span>
                    {apiUnavailableReason && (
                      <span className="truncate text-[10px] leading-3 text-muted-foreground">
                        {apiUnavailableReason}
                      </span>
                    )}
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={!meetingBotEnabled}
                  title={meetingBotUnavailableReason ?? 'Configure Teams Meeting Bot'}
                  onSelect={(event) => {
                    if (!meetingBotEnabled) {
                      event.preventDefault();
                      return;
                    }
                    setMeetingBotWizardOpen(true);
                  }}
                >
                  <Bot className="mr-2 h-4 w-4" />
                  <div className="flex min-w-0 flex-col">
                    <span>Add Teams Meeting Bot</span>
                    {meetingBotUnavailableReason && (
                      <span className="truncate text-[10px] leading-3 text-muted-foreground">
                        {meetingBotUnavailableReason}
                      </span>
                    )}
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate({ to: '/connections/new-function', search: { edit: '' } })}>
                  <Code className="h-4 w-4 mr-2" />
                  Create Custom Function
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          }
        />
      )}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
      <Sheet open={meetingBotWizardOpen} onOpenChange={setMeetingBotWizardOpen}>
        <SheetContent className="w-full overflow-y-auto p-0 sm:max-w-[760px]">
          <div className="border-b px-6 py-4">
            <SheetHeader>
              <SheetTitle>Teams Meeting Bot</SheetTitle>
              <SheetDescription>
                Configure the tenant Microsoft 365 and Vexa connection used by Meeting Bot.
              </SheetDescription>
            </SheetHeader>
          </div>
          <div className="p-6">
            <ConnectionsRoleGate backHref="/connections" surface="Teams Meeting Bot connection">
              <TeamsMeetingBotWizard
                onComplete={() => setMeetingBotWizardOpen(false)}
                onCancel={() => setMeetingBotWizardOpen(false)}
              />
            </ConnectionsRoleGate>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
